import React, { useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

/**
 * Overflow menu for a comment in the admin panel. Owns its anchor state because
 * hooks cannot run inside the map that renders the comment list.
 *
 * Which entries appear depends on where the comment sits:
 *  - top-level, live        → Mark done / Reopen, Delete
 *  - reply, live            → Delete (replies are part of a thread, never "done")
 *  - deleted                → Restore, Delete (of the already-deleted row is moot)
 *  - live parent shown for context in the Deleted view → nothing
 */
const CommentActionsMenu = ({
  comment,
  isBusy = false,
  onResolve,
  onRestore,
  onDelete,
  size = "small",
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);
  const run = (action) => () => {
    closeMenu();
    action();
  };

  const isReply = comment.parentCommentId !== null && comment.parentCommentId !== undefined;
  const isDeleted = Boolean(comment.deletedAt);

  const items = [];
  if (isDeleted) {
    items.push(
      <MenuItem key="restore" onClick={run(onRestore)} disabled={isBusy}>
        Restore
      </MenuItem>,
    );
  } else {
    if (!isReply) {
      items.push(
        <MenuItem key="resolve" onClick={run(onResolve)} disabled={isBusy}>
          {comment.resolvedAt ? "Reopen" : "Mark done"}
        </MenuItem>,
      );
    }
    items.push(
      <MenuItem
        key="delete"
        onClick={run(onDelete)}
        disabled={isBusy}
        sx={{ color: theme.palette.error.main }}
      >
        Delete
      </MenuItem>,
    );
  }

  // A live comment pulled into the Deleted view for context has no action of its
  // own, so it does not get a menu at all.
  if (items.length === 0) return null;

  return (
    <>
      <Tooltip title="Comment actions">
        <IconButton
          size={size}
          aria-label={`Actions for the comment by ${comment.name}`}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          <MoreVertRoundedIcon fontSize={size === "small" ? "small" : "medium"} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {items}
      </Menu>
    </>
  );
};

export default CommentActionsMenu;
